import type { CSSProperties } from 'react'
import { ParallaxBackdrop } from './parallax-backdrop'

/*
 * The mission select's environment, a mission control deck: structural
 * bulkheads with lit seams and slit lamps at both screen edges, distant
 * command towers, translucent holographic panels with drifting reflections,
 * a honeycomb and dot-matrix texture that only shows toward the sides, a slow
 * scan light, glossy 3D objects half out of frame and pixels that flicker on
 * now and then. A lavender backlight rings the mission area; the middle stays
 * dark. Decoration only.
 */

const vars = (v: Record<string, string | number>) => v as CSSProperties

/** Fills the page gutter on wide screens, a slim rib on narrower ones. */
const GUTTER = 'w-[max(2.6vw,calc(50vw-700px))] max-w-[240px]'

// ---- bulkheads ----

const SEAMS = [180, 260, 340, 470, 530, 590, 720, 800]
// y, lime
const LAMPS: [number, boolean][] = [
  [140, true],
  [300, false],
  [420, false],
  [500, true],
  [640, false],
  [760, false],
  [860, true],
]

function Bulkhead({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 120 1000" preserveAspectRatio="none" className="h-full w-full" fill="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" className="mcd-body-dark" />
          <stop offset="0.65" className="mcd-body" />
          <stop offset="1" className="mcd-body-lit" />
        </linearGradient>
      </defs>
      {/* spine against the screen edge with a chamfered panel standing out from it */}
      <polygon points="0,0 52,0 52,70 96,120 96,880 52,930 52,1000 0,1000" fill={`url(#${id})`} />
      <polygon points="12,40 30,52 30,948 12,960" className="mcd-recess" />
      {SEAMS.map((y) => (
        <line key={y} x1="52" y1={y} x2="96" y2={y} className="mcd-seam" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      ))}
      <line x1="52" y1="120" x2="52" y2="880" className="mcd-seam" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      {LAMPS.map(([y, lime], index) => (
        <rect
          key={y}
          x="66"
          y={y}
          width="14"
          height="10"
          className="mcd-lamp"
          data-lime={lime}
          style={vars({ '--dur': `${3.4 + (index % 3)}s`, '--delay': `${-index * 1.3}s` })}
        />
      ))}
      <polyline
        points="52,0 52,70 96,120 96,880 52,930 52,1000"
        className="mcd-edge"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

// ---- distant command towers ----

// x, width, height, antenna
const TOWERS: [number, number, number, boolean][] = [
  [0, 46, 200, false],
  [52, 30, 150, true],
  [90, 56, 232, true],
  [154, 24, 120, false],
  [186, 40, 170, true],
  [234, 20, 90, false],
  [262, 34, 130, false],
  [304, 16, 70, false],
  [328, 26, 104, true],
  [362, 14, 56, false],
]

function Towers({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 400 270" className="h-auto w-full" fill="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="mcd-tower-top" />
          <stop offset="1" className="mcd-tower-top" stopOpacity="0" />
        </linearGradient>
      </defs>
      {TOWERS.map(([x, w, h, antenna], t) => {
        const top = 270 - h
        const rows = Math.floor((h - 30) / 22)
        return (
          <g key={x}>
            <polygon
              points={`${x},270 ${x},${top + 10} ${x + 10},${top} ${x + w},${top} ${x + w},270`}
              fill={`url(#${id})`}
            />
            <polyline points={`${x},${top + 40} ${x},${top + 10} ${x + 10},${top} ${x + w},${top}`} className="mcd-tower-edge" strokeWidth="1" />
            {Array.from({ length: rows }, (_, r) =>
              (t + r) % 3 === 0 ? (
                <rect
                  key={r}
                  x={x + w / 2 - 2}
                  y={top + 16 + r * 22}
                  width="4"
                  height="4"
                  className="mcd-lamp"
                  data-lime={(t + r) % 6 === 3}
                  style={vars({ '--dur': `${4 + (r % 3)}s`, '--delay': `${-(t + r)}s` })}
                />
              ) : null,
            )}
            {antenna && (
              <>
                <line x1={x + w / 2} y1={top} x2={x + w / 2} y2={top - 24} className="mcd-tower-edge" strokeWidth="1" />
                <circle
                  cx={x + w / 2}
                  cy={top - 26}
                  r="2.2"
                  className="mcd-lamp"
                  data-lime="true"
                  style={vars({ '--dur': `${2.6 + (t % 3)}s`, '--delay': `${-t}s` })}
                />
              </>
            )}
          </g>
        )
      })}
    </svg>
  )
}

// ---- holographic panels ----

const SHAPE = '20,0 200,0 200,236 176,260 0,260 0,20'
const LINES = Array.from({ length: 22 }, (_, i) => 14 + i * 11)
const WAVE = Array.from({ length: 22 }, (_, i) => {
  const x = 16 + i * 8
  const y = 120 + Math.sin(i * 0.7) * 18 + Math.sin(i * 1.9) * 7
  return `${x},${y.toFixed(1)}`
}).join(' ')
const BARS = [22, 36, 18, 44, 30, 52, 26, 40, 16, 34]

function Holo({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 200 260" className="h-auto w-full overflow-visible" fill="none">
      <defs>
        <linearGradient id={`${id}-fill`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" className="mcd-holo-hi" />
          <stop offset="1" className="mcd-holo-lo" />
        </linearGradient>
        <linearGradient id={`${id}-sheen`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" className="mcd-sheen-a" stopOpacity="0" />
          <stop offset="0.42" className="mcd-sheen-a" stopOpacity="0.26" />
          <stop offset="0.58" className="mcd-sheen-b" stopOpacity="0.16" />
          <stop offset="1" className="mcd-sheen-a" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <polygon points={SHAPE} />
        </clipPath>
      </defs>
      <polygon points={SHAPE} fill={`url(#${id}-fill)`} />
      <g clipPath={`url(#${id}-clip)`}>
        {LINES.map((y) => (
          <line key={y} x1="0" y1={y} x2="200" y2={y} className="mcd-holo-line" strokeWidth="1" />
        ))}
        <polyline points={WAVE} className="mcd-holo-wave" strokeWidth="1.5" />
        {BARS.map((h, i) => (
          <rect key={i} x={24 + i * 16} y={232 - h} width="9" height={h} className="mcd-holo-bar" />
        ))}
        <g transform="skewX(-20)">
          <rect x="-90" y="-20" width="80" height="300" fill={`url(#${id}-sheen)`} className="mcd-sheen" />
        </g>
      </g>
      <polygon points={SHAPE} className="mcd-holo-rim" strokeWidth="1" />
      <path d="M0 46V20L20 0H52" className="mcd-holo-tick" strokeWidth="2" />
      <path d="M200 210V236L176 260H148" className="mcd-holo-tick" strokeWidth="2" />
    </svg>
  )
}

// ---- glossy 3D objects ----

function Octahedron({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 160 232" className="mcd-solid h-auto w-full">
      <defs>
        <linearGradient id={`${id}-a`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="mcd-g-hi" />
          <stop offset="1" className="mcd-g-lav" />
        </linearGradient>
        <linearGradient id={`${id}-b`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="mcd-g-lav" />
          <stop offset="1" className="mcd-g-deep" />
        </linearGradient>
        <linearGradient id={`${id}-c`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="mcd-g-deep" />
          <stop offset="1" className="mcd-g-navy" />
        </linearGradient>
      </defs>
      <g fillOpacity="0.92">
        <polygon points="80,0 6,108 66,132" fill={`url(#${id}-a)`} />
        <polygon points="80,0 66,132 154,100" fill={`url(#${id}-b)`} />
        <polygon points="6,108 66,132 80,232" fill={`url(#${id}-b)`} />
        <polygon points="66,132 154,100 80,232" fill={`url(#${id}-c)`} />
      </g>
      <polygon points="74,16 80,8 30,98 20,102" className="mcd-shine" opacity="0.4" />
      <polyline points="80,0 66,132 80,232" className="mcd-rim" fill="none" strokeWidth="1.2" opacity="0.8" />
      <polyline points="6,108 66,132 154,100" className="mcd-rim-lav" fill="none" strokeWidth="1" opacity="0.6" />
    </svg>
  )
}

function DataCore() {
  return (
    <svg viewBox="0 0 200 200" className="mcd-solid h-auto w-full">
      <defs>
        <linearGradient id="mcd-core-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="mcd-g-hi" />
          <stop offset="1" className="mcd-g-lav" />
        </linearGradient>
        <linearGradient id="mcd-core-l" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="mcd-g-lav" />
          <stop offset="1" className="mcd-g-navy" />
        </linearGradient>
        <linearGradient id="mcd-core-f" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="mcd-g-deep" />
          <stop offset="1" className="mcd-g-navy" />
        </linearGradient>
      </defs>
      <g fillOpacity="0.92">
        <polygon points="20,40 60,62 60,172 20,150" fill="url(#mcd-core-l)" />
        <polygon points="60,62 140,62 140,172 60,172" fill="url(#mcd-core-f)" />
        <polygon points="140,62 180,40 180,150 140,172" fill="url(#mcd-core-f)" />
        <polygon points="20,40 60,18 140,18 180,40 140,62 60,62" fill="url(#mcd-core-top)" />
      </g>
      {/* lime power band around the core */}
      <polyline points="20,100 60,122 140,122 180,100" className="mcd-band" fill="none" strokeWidth="5" />
      <polyline points="20,110 60,132 140,132 180,110" className="mcd-band" fill="none" strokeWidth="1.5" opacity="0.6" />
      <polygon points="52,24 70,22 40,40 30,38" className="mcd-shine" opacity="0.4" />
      <polyline points="20,40 60,62 140,62 180,40" className="mcd-rim-lav" fill="none" strokeWidth="1" opacity="0.7" />
      <line x1="60" y1="62" x2="60" y2="172" className="mcd-rim-lav" strokeWidth="1" opacity="0.5" />
      <line x1="140" y1="62" x2="140" y2="172" className="mcd-rim" strokeWidth="1.2" opacity="0.6" />
    </svg>
  )
}

function Pyramid() {
  return (
    <svg viewBox="0 0 100 100" className="mcd-solid h-auto w-full">
      <defs>
        <linearGradient id="mcd-pyr-a" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="mcd-g-lime" />
          <stop offset="1" className="mcd-g-deep" />
        </linearGradient>
        <linearGradient id="mcd-pyr-b" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="mcd-g-lav" />
          <stop offset="1" className="mcd-g-navy" />
        </linearGradient>
      </defs>
      <g fillOpacity="0.92">
        <polygon points="50,0 0,78 44,100" fill="url(#mcd-pyr-a)" />
        <polygon points="50,0 44,100 100,74" fill="url(#mcd-pyr-b)" />
      </g>
      <line x1="50" y1="0" x2="44" y2="100" className="mcd-rim" strokeWidth="1.2" opacity="0.8" />
    </svg>
  )
}

// ---- pixels ----

// left %, top %, size px, duration s, delay s, lime, sideways drift px
const PIXELS: [number, number, number, number, number, boolean, number][] = [
  [3, 12, 4, 7, 0, true, 10],
  [7, 31, 3, 9, -3, false, -8],
  [11, 54, 5, 8, -5, false, 12],
  [4, 72, 3, 10, -1, true, -6],
  [9, 90, 4, 7.5, -6, false, 8],
  [15, 20, 3, 11, -4, false, 6],
  [96, 8, 4, 8, -2, false, -10],
  [92, 24, 3, 9.5, -6, true, 8],
  [97, 46, 5, 7, -1, false, -12],
  [89, 62, 3, 10, -4, false, 6],
  [95, 82, 4, 8.5, -7, true, -8],
  [86, 94, 3, 9, -3, false, 10],
  [26, 4, 3, 10, -5, false, 8],
  [72, 3, 3, 8, -8, true, -6],
  [58, 6, 2, 9, -2, false, 6],
  [34, 97, 3, 8, -6, false, -8],
  [66, 96, 4, 10, -1, true, 10],
  [50, 2, 2, 11, -9, false, -6],
]

export function MissionControlBackdrop() {
  return (
    <ParallaxBackdrop>
      <div className="bgx-fade-y absolute inset-0 overflow-hidden">
        {/* layered violet atmosphere that shifts slowly */}
        <div className="mcd-atmos absolute inset-0" />
        <div className="mcd-shift absolute -inset-[10%]" />

        {/* digital textures, visible only toward the sides */}
        <div className="mcd-matrix absolute inset-0" />
        <svg className="mcd-hex absolute inset-0 h-full w-full" aria-hidden>
          <defs>
            <pattern id="mcd-hex" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 66L0 50L0 16L28 0L56 16L56 50L28 66L28 100" fill="none" stroke="currentColor" strokeWidth="1" />
              <path d="M28 0L28 34L0 50L0 84L28 100L56 84L56 50L28 34" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#mcd-hex)" />
        </svg>

        {/* distant command towers */}
        <div
          className="bgx-layer absolute left-0 top-[5%] hidden w-[clamp(200px,24vw,440px)] opacity-60 md:block [mask-image:linear-gradient(90deg,#000_40%,transparent)]"
          style={vars({ '--depth': 24 })}
        >
          <Towers id="mcd-tw-a" />
        </div>
        <div
          className="bgx-layer absolute right-0 top-[3%] hidden w-[clamp(200px,24vw,440px)] -scale-x-100 opacity-50 md:block [mask-image:linear-gradient(90deg,#000_40%,transparent)]"
          style={vars({ '--depth': 24 })}
        >
          <Towers id="mcd-tw-b" />
        </div>
        <div
          className="bgx-layer absolute left-0 top-[60%] hidden w-[clamp(160px,17vw,320px)] -scale-x-100 opacity-35 lg:block [mask-image:linear-gradient(270deg,#000_40%,transparent)]"
          style={vars({ '--depth': 18 })}
        >
          <Towers id="mcd-tw-c" />
        </div>
        <div
          className="bgx-layer absolute right-0 top-[64%] hidden w-[clamp(160px,17vw,320px)] opacity-30 lg:block [mask-image:linear-gradient(270deg,#000_40%,transparent)]"
          style={vars({ '--depth': 18 })}
        >
          <Towers id="mcd-tw-d" />
        </div>

        {/* pointer-led lavender light behind the missions */}
        <div className="absolute left-1/2 top-[42%] hidden md:block">
          <div className="bgx-follow" style={vars({ '--pull': 140 })}>
            <span className="mcd-pool absolute left-0 top-0 h-[70vh] w-[70vw] -translate-x-1/2 -translate-y-1/2" />
          </div>
        </div>

        {/* keep the middle dark and calm */}
        <div className="mcd-core absolute inset-0" />

        {/* slow scan light, brightest at the edges */}
        <div className="mcd-sweep absolute inset-x-0 top-0 h-full">
          <div className="mcd-sweep-band -mt-[180px] h-[180px]" />
          <div className="mcd-sweep-line h-px" />
        </div>

        {/* bulkheads at both edges */}
        <div
          className={`bgx-layer absolute left-0 top-[8%] hidden h-[84%] lg:block [mask-image:linear-gradient(180deg,transparent,#000_10%,#000_90%,transparent)] ${GUTTER}`}
          style={vars({ '--depth': 60 })}
        >
          <Bulkhead id="mcd-bh-l" />
        </div>
        <div
          className={`bgx-layer absolute right-0 top-[10%] hidden h-[84%] -scale-x-100 lg:block [mask-image:linear-gradient(180deg,transparent,#000_10%,#000_90%,transparent)] ${GUTTER}`}
          style={vars({ '--depth': 80 })}
        >
          <Bulkhead id="mcd-bh-r" />
        </div>

        {/* holographic panels tilted toward the middle */}
        <div className="bgx-layer absolute -left-[2vw] top-[17%] hidden w-[clamp(110px,11vw,200px)] lg:block" style={vars({ '--depth': 100 })}>
          <div className="mcd-float" style={vars({ '--dur': '13s' })}>
            <div className="mcd-holo" style={vars({ '--ry': '30deg', '--sheen-delay': '0s' })}>
              <Holo id="mcd-holo-a" />
            </div>
          </div>
        </div>
        <div className="bgx-layer absolute -right-[3vw] top-[30%] hidden w-[clamp(120px,12vw,220px)] md:block" style={vars({ '--depth': 120 })}>
          <div className="mcd-float" style={vars({ '--dur': '15s', '--delay': '-4s' })}>
            <div className="mcd-holo" style={vars({ '--ry': '-30deg', '--sheen-delay': '-4s' })}>
              <Holo id="mcd-holo-b" />
            </div>
          </div>
        </div>
        <div className="bgx-layer absolute -left-[1vw] top-[63%] hidden w-[clamp(90px,9vw,160px)] lg:block" style={vars({ '--depth': 90 })}>
          <div className="mcd-float" style={vars({ '--dur': '11s', '--delay': '-7s' })}>
            <div className="mcd-holo" style={vars({ '--ry': '26deg', '--sheen-delay': '-7s' })}>
              <Holo id="mcd-holo-c" />
            </div>
          </div>
        </div>

        {/* glossy 3D objects, half out of frame */}
        <div className="bgx-layer absolute -right-[3vw] top-[4%] hidden w-[clamp(80px,8vw,150px)] opacity-80 md:block" style={vars({ '--depth': 140 })}>
          <div className="mcd-hover" style={vars({ '--dur': '12s' })}>
            <Octahedron id="mcd-oct-a" />
          </div>
        </div>
        <div className="bgx-layer absolute -right-[4vw] top-[53%] hidden w-[clamp(110px,11vw,210px)] opacity-75 lg:block" style={vars({ '--depth': 110 })}>
          <div className="mcd-hover" style={vars({ '--dur': '16s', '--delay': '-5s' })}>
            <DataCore />
          </div>
        </div>
        <div className="bgx-layer absolute right-[3%] top-[90%] hidden w-[clamp(40px,3.6vw,64px)] opacity-80 lg:block" style={vars({ '--depth': 70 })}>
          <div className="mcd-hover" style={vars({ '--dur': '9s', '--delay': '-2s' })}>
            <Pyramid />
          </div>
        </div>
        <div className="bgx-layer absolute left-[2%] top-[88%] hidden w-[clamp(34px,3vw,54px)] opacity-70 lg:block" style={vars({ '--depth': 50 })}>
          <div className="mcd-hover" style={vars({ '--dur': '10s', '--delay': '-6s' })}>
            <Octahedron id="mcd-oct-b" />
          </div>
        </div>

        {/* lime rails at the screen edges where the bulkheads don't show */}
        <span className="mcd-rail absolute inset-y-[10%] left-1 w-px lg:hidden" />
        <span className="mcd-rail absolute inset-y-[10%] right-1 w-px lg:hidden" />

        {/* pixels that flicker on now and then */}
        {PIXELS.map(([left, top, size, duration, delay, lime, dx], index) => (
          <span
            key={`${left}-${top}`}
            className={`mcd-px ${index % 3 === 2 ? 'hidden md:block' : ''}`}
            data-lime={lime}
            style={vars({
              left: `${left}%`,
              top: `${top}%`,
              width: size,
              height: size,
              '--dur': `${duration}s`,
              '--delay': `${delay}s`,
              '--dx': `${dx}px`,
            })}
          />
        ))}
      </div>
    </ParallaxBackdrop>
  )
}
